/**
 * Everything about what the traveller pays, on the page the menu opens.
 *
 * This showed the plan and three links. "Upgrade" pointed at /pricing — the
 * public sales page, outside the app, with no navigation and nothing about the
 * plan they already have — and the invoices were a page further in, behind
 * "Subscription Settings", which is not where anyone looks for them. Both are
 * here now: changing plan stays inside the app, and the invoices and payments
 * are on the page rather than one click past it.
 */

import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import { useSubscriptionStore } from "@/stores/subscriptionStore";
import { openCustomerPortal } from "@/services/stripe";
import { useAuthStore } from "@/stores/authStore";
import {
  Crown,
  CreditCard,
  Calendar,
  Clock,
  Settings,
  ExternalLink,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BillingDocuments } from "@/components/subscription/BillingDocuments";

export default function SubscriptionDashboardPage() {
  const { token } = useAuthStore();
  const fmt = useLocaleFormat();
  const { currentSubscription, isLoading, fetchSubscription } =
    useSubscriptionStore();
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (token) fetchSubscription(token);
  }, [token, fetchSubscription]);

  const tier = currentSubscription?.tier || "FREE";
  const status = currentSubscription?.status || "none";
  const type = currentSubscription?.type || "none";

  const tierColors: Record<string, string> = {
    FREE: "bg-gray-100 text-gray-800 border-gray-200",
    PLUS: "bg-blue-100 text-blue-800 border-blue-200",
    PREMIUM: "bg-amber-100 text-amber-800 border-amber-200",
  };

  const statusLabels: Record<string, { label: string; color: string }> = {
    none: { label: "No subscription", color: "text-gray-500" },
    trialing: { label: "Trial active", color: "text-blue-600" },
    active: { label: "Active", color: "text-green-600" },
    canceled: { label: "Canceled", color: "text-red-600" },
    past_due: { label: "Payment overdue", color: "text-orange-600" },
    expired: { label: "Expired", color: "text-gray-500" },
  };

  const handleManageBilling = async () => {
    if (!token) return;
    setPortalLoading(true);
    try {
      await openCustomerPortal(token);
    } catch {
      setPortalLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Subscription</h1>
        <p className="text-gray-500 mt-1">Manage your plan and billing</p>
      </div>

      {/* Current Plan Card */}
      <div
        className={`rounded-xl border-2 p-6 ${tierColors[tier] || tierColors.FREE}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="h-8 w-8" />
            <div>
              <h2 className="text-2xl font-bold">{tier}</h2>
              <p
                className={`text-sm font-medium ${statusLabels[status]?.color || "text-gray-500"}`}
              >
                {statusLabels[status]?.label || status}
              </p>
            </div>
          </div>

          {type === "single_trip" &&
            currentSubscription?.daysRemaining != null && (
              <div className="flex items-center gap-2 bg-white/80 rounded-lg px-4 py-2">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-medium">
                  {currentSubscription.daysRemaining} days remaining
                </span>
              </div>
            )}
        </div>

        {currentSubscription?.currentPeriodEnd && type === "subscription" && (
          <div className="mt-4 flex items-center gap-2 text-sm opacity-80">
            <Calendar className="h-4 w-4" />
            <span>
              Next billing: {fmt.date(currentSubscription.currentPeriodEnd)}
            </span>
          </div>
        )}

        {currentSubscription?.singleTripExpiresAt && type === "single_trip" && (
          <div className="mt-4 flex items-center gap-2 text-sm opacity-80">
            <Calendar className="h-4 w-4" />
            <span>
              Expires: {fmt.date(currentSubscription.singleTripExpiresAt)}
            </span>
          </div>
        )}
      </div>

      {/* Action Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {tier !== "PREMIUM" && (
          <Link
            to="/traveler/subscription/plans"
            className="flex items-center gap-4 p-5 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
          >
            <div className="bg-blue-100 rounded-lg p-2">
              <CheckCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">
                {tier === "FREE" ? "Choose a plan" : "Change plan"}
              </h3>
              <p className="text-sm text-gray-500">
                Compare Plus, Premium and single-trip cover
              </p>
            </div>
          </Link>
        )}

        {currentSubscription?.hasStripeCustomer && (
          <button
            onClick={handleManageBilling}
            disabled={portalLoading}
            className="flex items-center gap-4 p-5 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm transition text-left"
          >
            <div className="bg-green-100 rounded-lg p-2">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900">Manage Billing</h3>
              <p className="text-sm text-gray-500">
                Payment methods, invoices, cancel
              </p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400" />
          </button>
        )}

        <Link
          to="/traveler/subscription/settings"
          className="flex items-center gap-4 p-5 rounded-xl border border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm transition"
        >
          <div className="bg-purple-100 rounded-lg p-2">
            <Settings className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              Subscription Settings
            </h3>
            <p className="text-sm text-gray-500">
              Preferences and notification settings
            </p>
          </div>
        </Link>
      </div>

      {/* What has been charged, and the paperwork for it. */}
      <BillingDocuments />

      {/* Past Due Warning */}
      {status === "past_due" && (
        <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-200 rounded-xl">
          <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-orange-900">Payment Overdue</h4>
            <p className="text-sm text-orange-700 mt-1">
              Please update your payment method to keep your subscription
              active.
            </p>
            <button
              onClick={handleManageBilling}
              className="mt-2 text-sm font-medium text-orange-700 underline"
            >
              Update payment method →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
